import { Member } from "@flow/common/types";
import { ActionComponentWrapper } from "@flow/frontend/member/components/ActionComponentWrapper";
import { MainContainer } from "@flow/frontend/member/components/MainContainer";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { ActionComponentProps, mergeContexts } from "@flow/frontend/member/plugin_api";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Link,
  PaletteColor,
  Stack,
  TextField,
  Typography,
  darken,
  useTheme,
} from "@mui/material";
import { parseFullName } from "parse-full-name";
import * as React from "react";
import { DonateActionCustomData, DonationBoostConfig } from "../../common/action_types";
import * as routes from "../../common/routes";
import { SmartDebitCheckout } from "./SmartDebitCheckout";
import { StripeCheckout } from "./StripeCheckout";
import { PaypalCheckout } from "./PaypalCheckout";
import { StripePaymentRequestButton } from "./StripePaymentRequestButton";
import LockIcon from "@mui/icons-material/Lock";
import { useUserAnalyticsService } from "@flow/frontend/member/user_analytics_service";
import { MakeItMonthlyButtons } from "./MakeItMonthlyButtons";
import * as stripeJs from "@stripe/stripe-js";
import { DonationTracker } from "./DonationTracker";

/* IDEA make this configurable in Flow settings*/
const DEFAULT_DONATION = 0;
const MIN_DONATION = 1;

export function stringToDecimal(amount: string): number {
  let n = Number.parseFloat(amount);
  n = n >= MIN_DONATION ? n : MIN_DONATION;
  return Number.parseFloat(n.toFixed(2));
}

/**
 * Format an amount such that we will show either a whole number, or if the number includes pence, to two decimal places
 *  ie- '50' instead of '50.00' but still show '5.50'
 * @param amount - the amount to be formatted
 */
export function floatOrInteger(amount: number) {
  return Number.isInteger(amount) ? amount : amount.toFixed(2);
}

function replaceMergeTags(text: string, name: string) {
  const memberFirstName = parseFullName(name).first || name;
  return text.replace(/{ member_name }/gm, memberFirstName);
}

/**
  Call `loadStripe` outside of a component’s render to avoid recreating the `Stripe` object on every render.
  The DonateAction component passes this promise to its children.
  We pass the Promise rather than the resulting String so that the children can manage
  the loading state while Promise is unresolved. And so that the Stripe object is only created once.
 */
const stripePromise = routes.getStripePublicKey.call().then(({ public_key }) => {
  if (public_key === undefined) {
    throw new Error("Failed to load Stripe public key from server");
  }
  return stripeJs.loadStripe(public_key);
});

export function DonateAction(props: ActionComponentProps<DonateActionCustomData>) {
  const { captureDonation } = useUserAnalyticsService();

  const [amount, setAmount] = React.useState(
    Number.parseFloat(props.ctx.actionData.donation_amount) || DEFAULT_DONATION
  );

  // allows us to pass in regular_donation=true in the URL params and set it to a monthly donate otherwise will default to props.custom.monthly_only
  const [isRegular, setIsRegular] = React.useState(
    props.ctx.actionData.regular_donation ? true : props.custom.monthly_only
  );

  const [usingCustomAmount, setUsingCustomAmount] = React.useState(false);
  const [customAmountValue, setCustomAmountValue] = React.useState<string>("");
  const [paymentGateway, setPaymentGateway] = React.useState<"stripe" | "smartdebit" | undefined>(undefined);
  const [useMemberPrefill, setUseMemberPrefill] = React.useState<boolean>(
    false
    /** Server-side member prefill disabled for donations. It won't work
     * with payment gateways that expect payer data to come from the client-side,
     * and in general names on petitions etc will not match names on payment cards.
     *
     * !props.takenAction && props.redacted_prefill !== undefined && props.redacted_prefill.has_email
     */
  );

  // React State for Donation Boost selection
  const [includeDonationBoost, setIncludeDonationBoost] = React.useState<boolean>(props.custom.boost_config.default);

  // React state to manage if the donor is making an anonymous donation
  const [anonymousDonation, setAnonymousDonation] = React.useState(false);

  React.useEffect(
    () => props.dispatch.setBackground(props.custom.bg_image_url, props.custom.bg_colour_hex),
    [props.custom.bg_image_url, props.custom.bg_colour_hex]
  );

  let personalisedTitle;
  if (props.ctx.member.name && props.custom.personalised_title) {
    personalisedTitle = replaceMergeTags(props.custom.personalised_title, props.ctx.member.name);
  }

  const { dark, main, contrastText } = memberTheme.palette.primary;
  const { grey300 } = memberTheme.palette.greys;

  const nextStep = () => {
    props.custom.bg_image_url = "";
    props.dispatch.gotoNextStep("skipped", props.ctx, props.consents || []);
  };

  function handlePaymentSuccess(donation_attempt_guid: string, member: Member) {
    const context = mergeContexts(props.ctx, { member });
    routes.recordDonationSuccess
      .call({
        donation_attempt_guid,
        context,
        action_guid: props.action.guid,
        use_prefill_member_data: useMemberPrefill,
      })
      .then(() => {
        // Make donation_attempt_guid attempt available for future reference.
        context.actionData.donation_attempt_guid = donation_attempt_guid;
        // Create regular_donation_made_in_journey flag if this is a regular donation.
        context.actionData.regular_donation_made_in_journey =
          isRegular || context.actionData?.regular_donation_made_in_journey;
        props.dispatch.gotoNextStep("completed", context, props.consents || []);
      })
      .finally(() =>
        // donationAmount is passed in decimal representation. e.g. 1.23, rather than in lowest currently unit, 123
        captureDonation({ actionGuid: props.action.guid, donationAmount: amount, isRegularDonation: isRegular })
      );
  }

  function handleAmountButtonClick(amount: number) {
    setUsingCustomAmount(false);
    setCustomAmountValue("");
    setAmount(amount);
  }

  function handleChangeCustomAmount(amount: number) {
    if (!Number.isNaN(amount)) {
      setUsingCustomAmount(true);
      setCustomAmountValue(`${amount}`);
      setAmount(amount);
    }
  }

  const form = React.useRef<HTMLFormElement | null>(null);

  // The theme and palette code here is needed for the TTRSUPP-238 experiment.
  const theme = useTheme();
  const palette = theme.palette as unknown as Record<string, PaletteColor>;
  const buttonColourPalette: PaletteColor | undefined = props.custom.donation_frequency_button_colour
    ? palette[props.custom.donation_frequency_button_colour]
    : undefined;

  return (
    <>
      <ActionComponentWrapper>
        <>
          {props.custom.image_url && (
            <Box
              component="img"
              alt={props.custom.image_alt_text}
              sx={{ maxWidth: "100%", paddingBottom: "1rem", display: "block" }}
              src={props.custom.image_url}
            />
          )}

          {props.custom.blurb && (
            <MainContainer sx={{ marginBottom: "24px" }}>
              <Typography sx={{ typography: { xs: "subtitle1", md: "h5", marginBottom: "12px" } }} color={main}>
                {personalisedTitle ? personalisedTitle : props.custom.title}
              </Typography>
              <Typography variant="body1" dangerouslySetInnerHTML={{ __html: props.custom.blurb }}></Typography>
            </MainContainer>
          )}
          {props.custom.tracking_config.enabled && (
            <DonationTracker
              config={props.custom.tracking_config}
              totals={props.custom.tracking_totals}
              journey_guid={props.action.journey_guid}
            />
          )}

          <MainContainer>
            {props.custom.checkout_title ? (
              <>
                <Typography
                  sx={{
                    typography: { xs: "subtitle1", md: "h5" },
                  }}
                  color={main}
                >
                  {props.custom.checkout_title}
                </Typography>
                <Typography
                  variant="body1"
                  dangerouslySetInnerHTML={{ __html: props.custom.donation_widget_subheading }}
                ></Typography>
              </>
            ) : (
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{ __html: props.custom.donation_widget_subheading }}
              ></Typography>
            )}

            <Divider />

            <Grid container alignItems="center">
              <Grid item xs={9}>
                <Typography variant="subtitle1">
                  I&apos;m donating &pound;
                  {floatOrInteger(
                    calculateTotalDonationAmount(
                      amount,
                      getDonationBoostAmount(props.custom.boost_config),
                      includeDonationBoost
                    )
                  )}{" "}
                  {isRegular && "per month"}
                </Typography>
              </Grid>
              <Grid item xs={3} style={{ textAlign: "right" }}>
                <LockIcon style={{ color: "#000" }} />
              </Grid>
            </Grid>

            <Divider />

            <form
              ref={form}
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              {paymentGateway === undefined && (
                <>
                  {!props.custom.monthly_only && props.custom.use_buttons_for_make_it_monthly && (
                    <Box sx={{ marginY: "2rem" }}>
                      <MakeItMonthlyButtons
                        isRegular={isRegular}
                        setIsRegular={setIsRegular}
                        donationFrequencyButtonColour={props.custom.donation_frequency_button_colour}
                        spaceBetweenDonationFrequencyButtons={props.custom.space_between_donation_frequency_buttons}
                      />
                    </Box>
                  )}

                  <Grid container spacing={1} marginBottom="2rem">
                    {props.custom.amounts_available_to_select.slice(0, 6).map((number, index) => (
                      <Grid item key={index} xs={4}>
                        <Button
                          id={`donate-select-amount-${number}`}
                          fullWidth
                          size="large"
                          color="secondary"
                          onClick={() => handleAmountButtonClick(number)}
                          sx={
                            buttonColourPalette
                              ? {
                                  // these styles are part of the TTRSUPP-238 experiment.
                                  backgroundColor:
                                    !usingCustomAmount && amount === number ? buttonColourPalette.main : grey300,
                                  border:
                                    !usingCustomAmount && amount === number
                                      ? `1px solid ${buttonColourPalette.dark}`
                                      : "none",
                                  color:
                                    !usingCustomAmount && amount === number ? buttonColourPalette.contrastText : dark,
                                  "&:focus": {
                                    outline: "none",
                                  },
                                  ":hover": {
                                    backgroundColor:
                                      !usingCustomAmount && amount === number
                                        ? buttonColourPalette.main
                                        : darken(grey300, 0.1),
                                  },
                                }
                              : {
                                  backgroundColor: !usingCustomAmount && amount === number ? dark : grey300,
                                  color: !usingCustomAmount && amount === number ? contrastText : dark,
                                  "&:focus": {
                                    outline: "none",
                                  },
                                  ":hover": {
                                    backgroundColor:
                                      !usingCustomAmount && amount === number ? dark : darken(grey300, 0.1),
                                  },
                                }
                          }
                        >
                          <Typography variant="subtitle1">£{floatOrInteger(number)}</Typography>
                        </Button>
                      </Grid>
                    ))}
                  </Grid>

                  <TextField
                    variant="outlined"
                    label="Another amount"
                    placeholder="e.g. 200.00"
                    size="medium"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                      inputMode: "numeric",
                    }}
                    inputProps={{
                      min: MIN_DONATION,
                      pattern: "[0-9]*",
                    }}
                    value={customAmountValue}
                    onChange={(e) => handleChangeCustomAmount(stringToDecimal(e.target.value))}
                    sx={{ marginBottom: "1.5rem" }}
                    fullWidth
                  />

                  {!props.custom.monthly_only && !props.custom.use_buttons_for_make_it_monthly && (
                    <FormGroup sx={{ marginBottom: "1.5rem" }}>
                      <FormControlLabel
                        sx={{ marginBottom: "0px" }}
                        control={
                          <Checkbox
                            id="check_is_subscription"
                            checked={isRegular}
                            onChange={({ target: { checked } }) => setIsRegular(checked)}
                          />
                        }
                        label="I'd like to donate this amount monthly"
                      />
                    </FormGroup>
                  )}

                  {(props.custom.boost_config.enabled || props.custom.tracking_config.enabled) && (
                    <FormGroup sx={{ marginBottom: "1.5rem" }}>
                      {props.custom.boost_config.enabled && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={includeDonationBoost}
                              onChange={(e) => setIncludeDonationBoost(e.target.checked)}
                            />
                          }
                          label={getDonationBoostMessage(props.custom.boost_config)}
                        />
                      )}
                      {props.custom.tracking_config.enabled && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={anonymousDonation}
                              onChange={(e) => setAnonymousDonation(e.target.checked)}
                            />
                          }
                          label="Don't display my donation publicly"
                        />
                      )}
                    </FormGroup>
                  )}

                  <Stack spacing={3}>
                    <Button
                      id={isRegular ? "pay-by-dd" : "pay-by-card"}
                      fullWidth
                      disabled={amount < MIN_DONATION}
                      onClick={() => setPaymentGateway(isRegular ? "smartdebit" : "stripe")}
                      sx={
                        buttonColourPalette
                          ? {
                              // these styles are part of the TTRSUPP-238 experiment.
                              backgroundColor: buttonColourPalette.main,
                              border: `1px solid ${buttonColourPalette.dark}`,
                              color: buttonColourPalette.contrastText,
                              "&.Mui-disabled": {
                                borderColor: "transparent",
                              },
                              ":hover": {
                                backgroundColor: darken(buttonColourPalette.main, 0.1),
                                borderColor: darken(buttonColourPalette.dark, 0.1),
                              },
                            }
                          : {}
                      }
                    >
                      {isRegular ? "Direct Debit" : "Card Payment"} <ChevronRightIcon />
                    </Button>

                    <PaypalCheckout
                      ctx={props.ctx}
                      action_guid={props.action.guid}
                      variant_idx={props.variant_idx}
                      amount={calculateTotalDonationAmount(
                        amount,
                        getDonationBoostAmount(props.custom.boost_config),
                        includeDonationBoost
                      )}
                      metadata={{
                        boost_amount: calculateBoostDonationAmount(props.custom.boost_config, includeDonationBoost),
                      }}
                      hide_donation={anonymousDonation}
                      isRegular={isRegular}
                      onPaymentSuccess={handlePaymentSuccess}
                    />
                    {/* We are chosing not to support recurring mobile wallets payments at the moment */}
                    {/* Most of the infrastructure exists for us to enable this and the Org might decide to do that in the future */}
                    {!isRegular && (
                      <StripePaymentRequestButton
                        ctx={props.ctx}
                        stripePromise={stripePromise}
                        action_guid={props.action.guid}
                        variant_idx={props.variant_idx}
                        amount={calculateTotalDonationAmount(
                          amount,
                          getDonationBoostAmount(props.custom.boost_config),
                          includeDonationBoost
                        )}
                        metadata={{
                          boost_amount: calculateBoostDonationAmount(props.custom.boost_config, includeDonationBoost),
                        }}
                        hide_donation={anonymousDonation}
                        isRegular={isRegular}
                        useMemberPrefill={useMemberPrefill}
                        onPaymentSuccess={handlePaymentSuccess}
                      />
                    )}
                  </Stack>
                </>
              )}

              {paymentGateway === "stripe" && (
                <StripeCheckout
                  ctx={props.ctx}
                  stripePromise={stripePromise}
                  action_guid={props.action.guid}
                  variant_idx={props.variant_idx}
                  amount={calculateTotalDonationAmount(
                    amount,
                    getDonationBoostAmount(props.custom.boost_config),
                    includeDonationBoost
                  )}
                  metadata={{
                    boost_amount: calculateBoostDonationAmount(props.custom.boost_config, includeDonationBoost),
                  }}
                  hide_donation={anonymousDonation}
                  isRegular={isRegular}
                  onClickBack={() => setPaymentGateway(undefined)}
                  onPaymentSuccess={handlePaymentSuccess}
                  form={form}
                  redacted_prefill={props.redacted_prefill}
                  useMemberPrefill={useMemberPrefill}
                  setUseMemberPrefill={setUseMemberPrefill}
                />
              )}

              {paymentGateway === "smartdebit" && (
                <SmartDebitCheckout
                  ctx={props.ctx}
                  variant_idx={props.variant_idx}
                  amount={calculateTotalDonationAmount(
                    amount,
                    getDonationBoostAmount(props.custom.boost_config),
                    includeDonationBoost
                  )}
                  metadata={{
                    boost_amount: calculateBoostDonationAmount(props.custom.boost_config, includeDonationBoost),
                  }}
                  hide_donation={anonymousDonation}
                  isRegular={isRegular}
                  onClickBack={() => setPaymentGateway(undefined)}
                  onPaymentSuccess={handlePaymentSuccess}
                  form={form}
                  redacted_prefill={props.redacted_prefill}
                  useMemberPrefill={useMemberPrefill}
                  setUseMemberPrefill={setUseMemberPrefill}
                  action_guid={props.action.guid}
                />
              )}
            </form>

            {shouldDisplayDonationBoostInfo(props.custom.boost_config) && (
              <>
                <Divider />
                <Typography variant="subtitle1" sx={{ marginTop: "1.5rem" }}>
                  {getDisplayDonationBoostInfoHeader(props.custom.boost_config)}
                </Typography>
                <Typography marginTop="1rem" variant="body2">
                  {getDisplayDonationBoostInfoDetail(props.custom.boost_config)}
                </Typography>
              </>
            )}

            <Divider />

            <Typography variant="subtitle1">Other ways of giving</Typography>

            <Typography marginTop="1rem" variant="body2">
              If you&apos;re having trouble with our one-off or monthly donation form, please call us on{" "}
              <Link href="tel:+442078460093">020 7846 0093</Link> any day of the week and we’ll be happy to help.
            </Typography>

            <Typography marginTop="1rem" variant="body2">
              Send cheques payable to &quot;38 Degrees&quot;, Moor Place, 1 Fore St Ave, London, EC2Y 9DT. Include your
              postcode and email address.
            </Typography>

            <Typography marginTop="1rem" variant="body2">
              We want to be transparent and accountable about how we spend your donations - for more information please
              see our{" "}
              <Link href="https://home.38degrees.org.uk/about/#donation-policy" target="_blank" rel="noopener">
                donation policy
              </Link>
              .
            </Typography>

            <Typography marginTop="1rem" variant="body2">
              Your personal information will be kept private and held securely. By submitting information you are
              agreeing to 38 Degrees keeping you informed about campaigns and agree to the use of cookies in accordance
              with our{" "}
              <Link href="https://home.38degrees.org.uk/privacy-policy" target="_blank" rel="noopener">
                privacy policy
              </Link>
              .
            </Typography>

            <Divider />

            {props.custom.display_skip_donation_button && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="medium"
                disableElevation
                onClick={nextStep}
                sx={{ backgroundColor: grey300, color: dark }}
              >
                Skip Donation <ChevronRightIcon />
              </Button>
            )}
          </MainContainer>
        </>
      </ActionComponentWrapper>
    </>
  );
}

// Donation Boost Utility Functions ---------------------------------------------------------------
//

export const calculateBoostDonationAmount = (config: DonationBoostConfig, isBoosted: boolean): number => {
  return isBoosted ? getDonationBoostAmount(config) : 0;
};

export const calculateTotalDonationAmount = (baseAmount: number, boostAmount: number, isBoosted: boolean) => {
  const total = isBoosted ? baseAmount + boostAmount : baseAmount;
  return baseAmount > 0 ? total : 0; // Ensure that the total amount is 0 if the base amount is 0
};

// If donation boost is disabled or the donation boost amount or message is not set, return 0.00;
// Otherwise return the configured donation boost amount as a number.
export const getDonationBoostAmount = (boost_config: DonationBoostConfig): number => {
  return boost_config.enabled ? boost_config.amount : 0;
};

export const getDonationBoostMessage = (boost_config: DonationBoostConfig) => {
  const donationBoostAmount = getDonationBoostAmount(boost_config);
  const AMOUNT_TAG = "{ donation_boost }";
  return boost_config.message.replace(AMOUNT_TAG, donationBoostAmount.toFixed(2));
};

export const shouldDisplayDonationBoostInfo = (boost_config: DonationBoostConfig) => {
  return boost_config.enabled && boost_config.info_header.length > 0;
};

export const getDisplayDonationBoostInfoHeader = (boost_config: DonationBoostConfig) => {
  return boost_config.enabled ? boost_config.info_header : "";
};

export const getDisplayDonationBoostInfoDetail = (boost_config: DonationBoostConfig) => {
  return boost_config.enabled ? boost_config.info_detail : "";
};
